/**
 * Advanced Image Optimizer Utility
 * This utility provides comprehensive image optimization with responsive handling
 */

/**
 * Creates a responsive srcset string for an image with multiple resolutions
 * @param {string} imagePath - Base image path
 * @param {string} format - Image format (jpg, webp)
 * @returns {string} - A srcset string with multiple resolution variants
 */
export const createSrcSet = (imagePath, format = 'jpg') => {
  // Base public URL path
  const basePath = process.env.PUBLIC_URL;
  
  // If it's a video, just return the original path
  if (imagePath.match(/\.(mp4|mov|webm)$/i)) {
    return imagePath;
  }
  
  // Extract the filename and extension
  const filename = imagePath.replace(`${basePath}/assets/`, '');
  const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
  
  // Generate srcset string with multiple resolutions
  return `
    ${basePath}/assets/optimized/responsive/${nameWithoutExt}_300.${format} 300w,
    ${basePath}/assets/optimized/responsive/${nameWithoutExt}_600.${format} 600w,
    ${basePath}/assets/optimized/responsive/${nameWithoutExt}_900.${format} 900w,
    ${basePath}/assets/optimized/responsive/${nameWithoutExt}_1200.${format} 1200w,
    ${basePath}/assets/optimized/responsive/${nameWithoutExt}_1800.${format} 1800w
  `.trim();
};

/**
 * Gets optimized image URL using WebP format with fallback
 * @param {string} imagePath - Original image path
 * @param {string} size - Size identifier
 * @returns {Object} - Object with webp and fallback URLs
 */
export const getOptimizedImageFormats = (imagePath, size = 'medium') => {
  // Base public URL path
  const basePath = process.env.PUBLIC_URL;
  
  // If it's a video, just return the original
  if (imagePath.match(/\.(mp4|mov|webm)$/i)) {
    return { webp: imagePath, fallback: imagePath };
  }
  
  // Extract the filename and extension
  const filename = imagePath.replace(`${basePath}/assets/`, '');
  const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
  const originalExt = filename.substring(filename.lastIndexOf('.') + 1);
  
  // Return object with WebP and original format URLs
  return {
    webp: `${basePath}/assets/optimized/${size}/${nameWithoutExt}.webp`,
    fallback: `${basePath}/assets/optimized/${size}/${filename}`
  };
};

/**
 * Generates a responsive image URL with proper sizing
 * @param {string} imagePath - Original image path
 * @param {string} size - Size identifier (thumbnail, small, medium, large, or original)
 * @returns {string} - Optimized image URL
 */
export const getOptimizedImageUrl = (imagePath, size = 'medium') => {
  // Base public URL path
  const basePath = process.env.PUBLIC_URL;
  
  // If the path includes a video extension, return the original
  if (imagePath.match(/\.(mp4|mov|webm)$/i)) {
    return imagePath;
  }
  
  // Extract the filename from the path
  const filename = imagePath.replace(`${basePath}/assets/`, '');
  
  // Return optimized image path based on requested size
  return `${basePath}/assets/optimized/${size}/${filename}`;
};

/**
 * Creates a complete responsive picture element configuration
 * @param {string} imagePath - Original image path
 * @param {string} size - Base size to use
 * @param {Object} options - Additional options like art direction
 * @returns {Object} - Complete configuration for a responsive picture element
 */
export const createResponsiveImageConfig = (imagePath, size = 'medium', options = {}) => {
  // Get formats for the main image
  const formats = getOptimizedImageFormats(imagePath, size);
  
  // Generate responsive srcsets
  const srcsets = {
    webp: createSrcSet(imagePath, 'webp'),
    original: createSrcSet(imagePath)
  };
  
  // Default sizes attribute for responsive images
  const defaultSizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw';
  
  // Art direction sources for different screen sizes (if provided)
  const artDirection = options.artDirection || null;
  
  return {
    sources: [
      // WebP source
      {
        type: 'image/webp',
        srcset: srcsets.webp,
        sizes: options.sizes || defaultSizes
      },
      // Original format source
      {
        type: `image/${formats.fallback.includes('.png') ? 'png' : 'jpeg'}`,
        srcset: srcsets.original,
        sizes: options.sizes || defaultSizes
      }
    ],
    // Fallback img attributes
    img: {
      src: formats.fallback,
      alt: options.alt || '',
      loading: options.loading || 'lazy',
      className: options.className || '',
      width: options.width,
      height: options.height
    },
    // Art direction for different screen sizes
    artDirection
  };
};

/**
 * Size Constants for the application
 */
export const IMAGE_SIZES = {
  THUMBNAIL: 'thumbnail', // 150x150
  SMALL: 'small',         // 300x200
  MEDIUM: 'medium',       // 600x400
  LARGE: 'large',         // 1200x800
  ORIGINAL: 'original'    // Original size
};

/**
 * Context-aware image sizing
 * Returns the appropriate size for different UI contexts
 */
export const getContextSize = (context) => {
  switch (context) {
    case 'hero':
      return IMAGE_SIZES.LARGE;
    case 'card':
      return IMAGE_SIZES.MEDIUM;
    case 'testimonial':
      return IMAGE_SIZES.MEDIUM;
    case 'background':
      return IMAGE_SIZES.LARGE;
    case 'thumbnail':
      return IMAGE_SIZES.THUMBNAIL;
    default:
      return IMAGE_SIZES.MEDIUM;
  }
}; 